import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import NewsletterSignup from "@/components/NewsletterSignup";
import { SERIES } from "@/lib/series";
import { productImg } from "@/lib/img";

const PAGE = 10;

function highlight(text, terms) {
  if (!text || !terms.length) return text;
  const re = new RegExp(`(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "ig");
  const parts = String(text).split(re);
  return parts.map((p, i) =>
    re.test(p) && terms.some((t) => p.toLowerCase() === t.toLowerCase())
      ? <mark key={i} className="bg-transparent text-[color:var(--vt-amber)] font-bold">{p}</mark>
      : p
  );
}

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [format, setFormat] = useState("");
  const [series, setSeries] = useState("");
  const [audience, setAudience] = useState("");
  const [sort, setSort] = useState("relevance");

  const [data, setData] = useState(null);
  const [content, setContent] = useState([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [newest, setNewest] = useState([]);

  const terms = useMemo(() => q.trim().toLowerCase().split(/\s+/).filter(Boolean), [q]);

  const load = useCallback(
    async (reset) => {
      if (q.trim().length < 2) { setData({ content_total: 0, groups: {} }); setContent([]); return; }
      setLoading(true);
      const off = reset ? 0 : offset;
      try {
        const { data: d } = await api.get("/search", {
          params: { q: q.trim(), format: format || undefined, series: series || undefined, audience: audience || undefined, sort, limit: PAGE, offset: off },
        });
        setData(d);
        setContent((prev) => (reset ? d.groups.content : [...prev, ...d.groups.content]));
      } finally {
        setLoading(false);
      }
    },
    [q, format, series, audience, sort, offset]
  );

  // reset + load whenever query/filters change
  useEffect(() => { setOffset(0); }, [q, format, series, audience, sort]);
  useEffect(() => { setInput(q); }, [q]);
  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [q, format, series, audience, sort]);
  useEffect(() => { if (offset > 0) load(false); /* eslint-disable-next-line */ }, [offset]);

  // newest posts for the no-results state
  useEffect(() => {
    (async () => {
      try { const { data: d } = await api.get("/articles?limit=3"); setNewest(d || []); } catch { /* ignore */ }
    })();
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setParams(input.trim() ? { q: input.trim() } : {});
  };

  const total = data?.content_total || 0;
  const otherGroups = data?.groups || {};
  const extras = [
    ...(otherGroups.series || []),
    ...(otherGroups.rulebook || []),
    ...(otherGroups.tools || []),
    ...(otherGroups.products || []),
  ];
  const grandTotal = total + extras.length;
  const showAudience = content.some((c) => c.audience) || audience;

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 pt-12 pb-20" data-testid="search-page">
      <Seo title="Search" description="Search VoluntoldHQ." path="/search" noindex />

      {/* search input */}
      <form onSubmit={submit} role="search" className="flex items-center gap-3 border-b-2 border-[color:var(--vt-dark)] pb-3">
        <SearchIcon size={24} className="text-[color:var(--vt-body-on-cream)] shrink-0" />
        <label htmlFor="search-page-input" className="sr-only">Search</label>
        <input
          id="search-page-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search stories, series, tools…"
          data-testid="search-page-input"
          className="flex-1 min-w-0 bg-transparent border-0 outline-none font-head uppercase text-3xl lg:text-5xl text-[color:var(--vt-body-on-cream)] placeholder:text-[#B4AC9C]"
        />
        <button type="submit" data-testid="search-page-submit" className="vt-btn vt-btn-secondary-cream vt-btn-sm">Search</button>
      </form>

      {/* count + filters */}
      {q.trim().length >= 2 && (
        <div className="mt-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4" data-testid="search-controls">
          <p className="text-[color:var(--vt-body-on-cream)]" aria-live="polite" data-testid="search-count">
            <span className="font-head text-2xl">{grandTotal}</span> {grandTotal === 1 ? "result" : "results"} for <span className="font-semibold">"{q}"</span>
          </p>
          <div className="flex flex-wrap gap-3">
            <Select label="Format" value={format} onChange={setFormat} testId="filter-format"
              options={[["", "All"], ["Read", "Read"], ["Watch", "Watch"]]} />
            <Select label="Series" value={series} onChange={setSeries} testId="filter-series"
              options={[["", "All series"], ...SERIES.map((s) => [s.name, s.name])]} />
            {showAudience && (
              <Select label="Audience" value={audience} onChange={setAudience} testId="filter-audience"
                options={[["", "Everyone"], ["coach", "Coaches"], ["parent", "Parents"], ["player", "Players"]]} />
            )}
            <Select label="Sort" value={sort} onChange={setSort} testId="filter-sort"
              options={[["relevance", "Relevance"], ["newest", "Newest"]]} />
          </div>
        </div>
      )}

      {/* extras (series / rulebook / tools / shop) */}
      {q.trim().length >= 2 && extras.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-3" data-testid="search-extras">
          {extras.map((r, i) => (
            <Link key={i} to={r.url} data-testid={`search-extra-${i}`}
              className="border-2 border-[color:var(--vt-dark)] px-4 py-2 hover:border-[color:var(--vt-amber)] transition-colors">
              <span className="vt-eyebrow vt-eyebrow--cream block">{r.type}</span>
              <span className="font-head text-lg text-[color:var(--vt-body-on-cream)]">{highlight(r.title, terms)}</span>
            </Link>
          ))}
        </div>
      )}

      {/* results list */}
      {q.trim().length >= 2 && (
        <div className="mt-8" data-testid="search-results">
          {content.map((r, i) => (
            <Link key={i} to={r.url} data-testid={`search-result-row-${i}`}
              className="flex gap-5 py-6 border-t border-[color:var(--vt-dark)]/15 group">
              {r.thumbnail && (
                <div className="hidden sm:block w-32 h-24 shrink-0 overflow-hidden border border-[color:var(--vt-dark)]/20">
                  <img {...productImg(r.thumbnail, "120px", [480])} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" style={{ filter: "grayscale(1)" }} />
                </div>
              )}
              <div className="min-w-0">
                <span className="vt-eyebrow vt-eyebrow--cream">
                  {(r.series || r.format || "Read")}{r.date ? ` · ${new Date(r.date).toLocaleDateString()}` : ""}
                </span>
                <h3 className="mt-1 font-head text-2xl lg:text-3xl text-[color:var(--vt-body-on-cream)] leading-tight group-hover:text-[color:var(--vt-amber)] transition-colors">
                  {highlight(r.title, terms)}
                </h3>
                <p className="mt-1 text-sm text-[color:var(--vt-body-on-cream)] line-clamp-2">{highlight(r.snippet, terms)}</p>
              </div>
            </Link>
          ))}

          {content.length < total && (
            <button onClick={() => setOffset(content.length)} disabled={loading}
              data-testid="search-load-more"
              className="mt-8 vt-btn vt-btn-secondary-cream">
              {loading ? "Loading…" : "Load more"}
            </button>
          )}
        </div>
      )}

      {/* no results */}
      {q.trim().length >= 2 && !loading && grandTotal === 0 && (
        <div className="mt-10" data-testid="search-no-results">
          <h2 className="font-head text-[color:var(--vt-body-on-cream)] text-5xl lg:text-7xl leading-[0.9]">
            NO CALL ON THAT ONE.
          </h2>
          <p className="mt-5 text-[color:var(--vt-body-on-cream)] text-lg">Try another word, or start here:</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/rulebook" data-testid="noresult-rulebook" className="vt-btn vt-btn-secondary-cream vt-btn-sm">The Rulebook →</Link>
            <Link to="/assessment" data-testid="noresult-assessment" className="vt-btn vt-btn-secondary-cream vt-btn-sm">Coach Assessment →</Link>
            <Link to="/tools/parent-script" data-testid="noresult-parent-script" className="vt-btn vt-btn-secondary-cream vt-btn-sm">Parent Script →</Link>
          </div>
          {newest.length > 0 && (
            <div className="mt-8">
              <span className="vt-eyebrow vt-eyebrow--cream">The Latest</span>
              <ul className="mt-3 space-y-2">
                {newest.map((a) => (
                  <li key={a.slug}>
                    <Link to={`/article/${a.slug}`} className="link-line text-[color:var(--vt-body-on-cream)] font-semibold">{a.title} →</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-10 max-w-lg border-t border-[color:var(--vt-dark)]/15 pt-8">
            <span className="vt-eyebrow vt-eyebrow--cream">Tell us what you want us to cover</span>
            <div className="mt-3">
              <NewsletterSignup source="search-no-results" tone="light" showCopy={false} testId="search-noresult-newsletter" />
            </div>
          </div>
        </div>
      )}

      {q.trim().length < 2 && (
        <p className="mt-10 text-[color:var(--vt-body-on-cream)]" data-testid="search-prompt">
          Type at least two letters to search VoluntoldHQ.
        </p>
      )}
    </div>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="vt-eyebrow vt-eyebrow--cream block mb-1">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="bg-white border-2 border-[color:var(--vt-dark)] px-3 py-2 text-sm text-[color:var(--vt-body-on-cream)] focus:outline-none focus:border-[color:var(--vt-amber)]"
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
